import { useState } from "react";
import { ArrowRight, CheckCircle, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MaskedLine, Reveal, Overline } from "../components/Kinetic";
import { TWO_A_URL } from "../components/EstimateCTA";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact"
        description="Questions about selling your electronics in Chicago? Send us a message or request a free buyback evaluation."
        path="/contact"
      />

      <section className="pt-32 md:pt-44">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Overline>Contact</Overline>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl">
            <MaskedLine delay={0.15}>TALK TO</MaskedLine>
            <MaskedLine delay={0.3}><span className="text-brand">A HUMAN.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-[#4A4A4A]">
              Questions about preparing a device, what we buy, or how an evaluation works? Send a message —
              or skip the line and request a buyback estimate directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-3xl border border-black/10 bg-[#F5F5F7] p-9" data-testid="contact-info-card">
                <h2 className="font-display text-xl font-bold tracking-tight">Before you write</h2>
                <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">
                  The fastest route to an answer about your specific device is the estimate page at{" "}
                  <a href={TWO_A_URL} target="_blank" rel="noopener" className="font-semibold text-navy underline underline-offset-2 hover:text-brand">2A Electronics</a>,
                  our associated Chicago business — quick and no obligation.
                </p>
                <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid="contact-estimate-btn"
                  className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy">
                  Get a Buyback Estimate
                  <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <div className="mt-8 flex flex-col gap-5 border-t border-black/10 pt-7 text-sm">
                  <p className="flex items-start gap-3 text-[#4A4A4A]">
                    <MapPin size={18} weight="duotone" className="mt-0.5 shrink-0 text-brand" />
                    Serving sellers across Chicago, Illinois — in-person evaluations by arrangement.
                  </p>
                  <p className="flex items-start gap-3 text-[#4A4A4A]">
                    <Clock size={18} weight="duotone" className="mt-0.5 shrink-0 text-brand" />
                    Estimate requests are answered within one business day.
                  </p>
                  <p className="flex items-start gap-3 text-[#4A4A4A]">
                    <EnvelopeSimple size={18} weight="duotone" className="mt-0.5 shrink-0 text-brand" />
                    Operated in association with{" "}
                    <a href="https://2aelectronics.com/" target="_blank" rel="noopener" className="font-semibold text-navy underline underline-offset-2 hover:text-brand">2A Electronics</a>.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              {sent ? (
                <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-black/10 bg-white p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-success">
                  <CheckCircle size={56} weight="duotone" className="text-cash" />
                  <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">Message received</h2>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#4A4A4A]">
                    Thanks, {form.name.split(" ")[0]}. We will get back to you at {form.email} within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="rounded-3xl border border-black/10 bg-white p-9 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="contact-form">
                  <h2 className="font-display text-xl font-bold tracking-tight">Send a message</h2>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name" data-testid="contact-name-input"
                      className="rounded-xl border border-black/15 px-4 py-3.5 text-sm outline-none transition-colors focus:border-brand" />
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      type="email" placeholder="Email address" data-testid="contact-email-input"
                      className="rounded-xl border border-black/15 px-4 py-3.5 text-sm outline-none transition-colors focus:border-brand" />
                  </div>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help?" rows={6} data-testid="contact-message-input"
                    className="mt-4 w-full rounded-xl border border-black/15 px-4 py-3.5 text-sm outline-none transition-colors focus:border-brand" />
                  <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                    className="group mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-brand disabled:opacity-50">
                    {sending ? "Sending…" : "Send Message"}
                    <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
